import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Truck, Eye, EyeOff } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6 py-16">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <Link to="/" className="flex items-center gap-2 font-heading font-black text-lg text-slate-900 mb-6 justify-center">
          <Truck className="w-6 h-6 text-brand shrink-0" />
          <span>RouteHaul <span className="text-slate-400 font-bold">Logistics Pathways</span></span>
        </Link>
        <div className="bg-white border border-slate-200 rounded-md p-8">
          <h1 className="font-heading text-2xl font-black tracking-tight text-slate-900">{title}</h1>
          <p className="text-slate-500 text-sm mt-1 mb-6">{subtitle}</p>
          {children}
          <button onClick={googleLogin} data-testid="google-login" className="mt-4 w-full h-11 rounded-md border border-slate-300 flex items-center justify-center gap-2 font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
            Continue with Google
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Log in to continue your guide.">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Input id="password" data-testid="login-password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 pr-11" />
            <button type="button" data-testid="login-password-toggle" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? "Hide password" : "Show password"} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors">
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        {err && <p className="text-sm text-red-600" data-testid="login-error">{err}</p>}
        <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full h-11 rounded-full bg-brand hover:bg-brand-hover text-white font-bold">
          {loading ? "Logging in…" : "Log in"}
        </Button>
      </form>
      <p className="text-sm text-slate-500 mt-4 text-center">
        No account? <Link to="/register" className="text-brand font-bold">Sign up</Link>
      </p>
    </AuthShell>
  );
}
